import { directSupabase } from '../lib/supabaseClient';

export type ActivityEntityType =
  | 'project'
  | 'document'
  | 'survey'
  | 'job'
  | 'email'
  | 'supplier_reference';

export type ActivityAction =
  | 'project.created'
  | 'project.customer_assigned'
  | 'project.customer_changed'
  | 'document.created'
  | 'document.linked'
  | 'document.status_changed'
  | 'document.sent'
  | 'document.deleted'
  | 'survey.final_created'
  | 'survey.final_designs_copied'
  | 'job_review.reviewed'
  | 'job_review.reopened'
  | 'survey.final_completed'
  | 'survey.final_reopened'
  | 'survey.final_reapproval_required'
  | 'survey.final_operational'
  | 'survey.final_live_updated_without_reapproval'
  | 'survey.final_reapproval_transmitted'
  | 'survey.final_reapproval_accepted'
  | 'survey.cleared'
  | 'job.completed'
  | 'job.released'
  | 'job.ended'
  | 'supplier.order_sent'
  | 'supplier.reference_added'
  | 'payment_schedule.created_from_quote'
  | 'payment_schedule.created_retrospective'
  | 'payment_schedule.changed'
  | 'payment_schedule.review_required'
  | 'payment_schedule.stage_issued'
  | 'payment_schedule.stage_rolled_back'
  | 'payment_schedule.removed';

export type ActivityEventInput = {
  action: ActivityAction;
  entityType: ActivityEntityType;
  label: string;
  entityId?: string | null;
  projectId?: string | null;
  metadata?: Record<string, unknown> | null;
  actorId?: string | null;
};

export type ActivityEventRow = {
  id: string;
  occurredAt: string;
  actorId: string | null;
  action: string;
  entityType: string;
  entityId: string | null;
  projectId: string | null;
  label: string;
  metadata: Record<string, unknown>;
};

async function resolveActorId(explicit?: string | null): Promise<string | null> {
  if (explicit) return explicit;
  try {
    const { data } = await directSupabase.auth.getUser();
    return data.user?.id || null;
  } catch {
    return null;
  }
}

/**
 * Fire-and-forget friendly emit. Never throws to callers — audit must not block business flows.
 */
export async function emitActivityEvent(input: ActivityEventInput): Promise<void> {
  try {
    const actorId = await resolveActorId(input.actorId);
    const payload = {
      actor_id: actorId,
      action: input.action,
      entity_type: input.entityType,
      entity_id: input.entityId || null,
      project_id: input.projectId || null,
      label: String(input.label || '').trim() || input.action,
      metadata: input.metadata && typeof input.metadata === 'object' ? input.metadata : {},
    };
    const { error } = await directSupabase.from('jarvis_activity_events').insert(payload);
    if (error) {
      console.warn('Activity event emit failed:', input.action, error.message);
    }
  } catch (err) {
    console.warn('Activity event emit failed:', input.action, err);
  }
}

export function emitActivityEventBackground(input: ActivityEventInput): void {
  void emitActivityEvent(input);
}

/** Stamp `created_by` when the caller has an authenticated user. */
export async function withProjectCreator<T extends Record<string, unknown>>(
  row: T,
): Promise<T & { created_by?: string }> {
  const actorId = await resolveActorId();
  if (!actorId) return row;
  return { ...row, created_by: actorId };
}

export function emitProjectCreatedActivity(args: {
  projectId: string;
  customerId?: string | number | null;
  customerName?: string | null;
  actorId?: string | null;
}): void {
  const projectId = String(args.projectId || '').trim();
  if (!projectId) return;
  emitActivityEventBackground({
    action: 'project.created',
    entityType: 'project',
    entityId: projectId,
    projectId,
    actorId: args.actorId,
    label: 'Project created',
  });
  if (args.customerId != null && String(args.customerId).trim()) {
    const name = String(args.customerName || '').trim();
    emitActivityEventBackground({
      action: 'project.customer_assigned',
      entityType: 'project',
      entityId: projectId,
      projectId,
      actorId: args.actorId,
      label: name ? `Customer linked (${name})` : 'Customer linked',
      metadata: {
        customerId: String(args.customerId),
        customerName: name || null,
      },
    });
  }
}

export function emitProjectCustomerChangedActivity(args: {
  projectId: string;
  previousCustomerId?: string | number | null;
  customerId?: string | number | null;
  customerName?: string | null;
  actorId?: string | null;
}): void {
  const projectId = String(args.projectId || '').trim();
  if (!projectId) return;
  const prev = args.previousCustomerId != null ? String(args.previousCustomerId).trim() : '';
  const next = args.customerId != null ? String(args.customerId).trim() : '';
  if (!next || prev === next) return;
  const name = String(args.customerName || '').trim();
  const hadPrevious = Boolean(prev);
  emitActivityEventBackground({
    action: hadPrevious ? 'project.customer_changed' : 'project.customer_assigned',
    entityType: 'project',
    entityId: projectId,
    projectId,
    actorId: args.actorId,
    label: hadPrevious
      ? (name ? `Customer changed (${name})` : 'Customer changed')
      : (name ? `Customer linked (${name})` : 'Customer linked'),
    metadata: {
      previousCustomerId: prev || null,
      customerId: next,
      customerName: name || null,
    },
  });
}

export async function listProjectActivityEvents(
  projectId: string,
  limit = 40,
): Promise<ActivityEventRow[]> {
  const id = String(projectId || '').trim();
  if (!id) return [];
  const { data, error } = await directSupabase
    .from('jarvis_activity_events')
    .select('id, occurred_at, actor_id, action, entity_type, entity_id, project_id, label, metadata')
    .eq('project_id', id)
    .order('occurred_at', { ascending: false })
    .limit(Math.max(1, limit));
  if (error) {
    // Table may not be migrated yet in some environments.
    console.warn('Failed to load activity events:', error.message);
    return [];
  }
  return (data || []).map((row: any) => ({
    id: String(row.id),
    occurredAt: String(row.occurred_at || ''),
    actorId: row.actor_id ? String(row.actor_id) : null,
    action: String(row.action || ''),
    entityType: String(row.entity_type || ''),
    entityId: row.entity_id ? String(row.entity_id) : null,
    projectId: row.project_id ? String(row.project_id) : null,
    label: String(row.label || ''),
    metadata: row.metadata && typeof row.metadata === 'object' && !Array.isArray(row.metadata)
      ? row.metadata as Record<string, unknown>
      : {},
  }));
}

export const activityEventsService = {
  emit: emitActivityEvent,
  emitBackground: emitActivityEventBackground,
  withProjectCreator,
  emitProjectCreated: emitProjectCreatedActivity,
  emitProjectCustomerChanged: emitProjectCustomerChangedActivity,
  listByProject: listProjectActivityEvents,
};
